import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { allProducts } from "@/mocks/productDetail";
import SeoHead from "@/components/feature/SeoHead";

interface CrowntalkShareProps {
  productName: string;
  productPrice: number;
  productImage: string;
}

function CrownChatShare({ productName, productPrice, productImage }: CrowntalkShareProps) {
  const [showModal, setShowModal] = useState(false);
  const [shared, setShared] = useState(false);

  const handleShare = () => {
    setShared(true);
    setTimeout(() => {
      setShared(false);
      setShowModal(false);
    }, 2000);
  };

  return (
    <>
      <div className="mt-4 p-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-100 rounded-xl flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-xl flex-shrink-0">
            <i className="ri-chat-smile-3-line text-white text-lg"></i>
          </div>
          <div>
            <p className="text-gray-800 font-semibold text-sm">通过 Crowntalk 分享给好友</p>
            <p className="text-gray-400 text-xs mt-0.5">发送礼物卡片，好友可直接购买</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
        >
          <i className="ri-share-line"></i>
          分享礼物
        </button>
      </div>

      {/* Share Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-red-500 rounded-lg">
                  <i className="ri-chat-smile-3-line text-white text-sm"></i>
                </div>
                <span className="font-bold text-gray-800">Crowntalk 分享</span>
              </div>
              <button onClick={() => setShowModal(false)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            {/* Gift card preview */}
            <div className="bg-gradient-to-br from-red-500 to-orange-500 rounded-xl p-4 mb-5 text-white">
              <div className="flex items-center gap-1 mb-3">
                <i className="ri-gift-line text-sm"></i>
                <span className="text-xs font-medium opacity-80">礼物卡片预览</span>
              </div>
              <div className="flex items-center gap-3">
                <img src={productImage} alt={productName} className="w-16 h-16 rounded-lg object-cover object-top flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold line-clamp-2 leading-snug mb-1">{productName}</p>
                  <p className="text-white/80 text-xs">皇冠鸿运商城</p>
                  <p className="text-white font-bold text-lg mt-1">¥{productPrice.toLocaleString()}</p>
                </div>
              </div>
              <div className="mt-3 bg-white/20 rounded-lg py-2 text-center text-xs font-medium">
                <i className="ri-shopping-bag-line mr-1"></i>点击查看商品详情
              </div>
            </div>

            {/* Contact list */}
            <p className="text-gray-500 text-xs mb-3">选择 Crowntalk 好友发送：</p>
            <div className="space-y-2 mb-5">
              {[
                { name: "Sarah M.", status: "在线", avatar: "S", color: "bg-blue-500" },
                { name: "田中 健", status: "刚刚活跃", avatar: "T", color: "bg-red-500" },
                { name: "李小明", status: "在线", avatar: "L", color: "bg-orange-500" },
              ].map((contact) => (
                <div key={contact.name} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors">
                  <div className={`w-9 h-9 flex items-center justify-center ${contact.color} rounded-full text-white font-bold text-sm flex-shrink-0`}>
                    {contact.avatar}
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-800 text-sm font-medium">{contact.name}</p>
                    <p className="text-gray-400 text-xs">{contact.status}</p>
                  </div>
                  <i className="ri-arrow-right-s-line text-gray-300"></i>
                </div>
              ))}
            </div>

            <button
              onClick={handleShare}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                shared ? "bg-green-500 text-white" : "bg-red-500 hover:bg-red-600 text-white"
              }`}
            >
              {shared ? (
                <><i className="ri-check-line mr-1"></i>礼物卡片已发送！</>
              ) : (
                <><i className="ri-send-plane-line mr-1"></i>发送礼物卡片</>
              )}
            </button>
            <p className="text-gray-400 text-xs text-center mt-3">
              好友收到后可直接在 Crowntalk 内购买
            </p>
          </div>
        </div>
      )}
    </>
  );
}

const tagColors: Record<string, string> = {
  爆款: "bg-red-500", 新品: "bg-green-500", 热销: "bg-orange-500",
  礼盒: "bg-pink-500", 限量: "bg-purple-500", 专业: "bg-blue-500", 奢品: "bg-amber-600",
};

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://www.crownhongyunmall.com";

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const product = allProducts.find((p) => p.id === Number(id));

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedStorage, setSelectedStorage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [activeTab, setActiveTab] = useState<"detail" | "reviews">("detail");

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-red-50 rounded-full mx-auto mb-4">
            <i className="ri-error-warning-line text-4xl text-red-300"></i>
          </div>
          <p className="text-gray-600 font-medium text-lg mb-2">商品不存在</p>
          <button onClick={() => navigate("/products")} className="bg-red-500 text-white px-6 py-2 rounded-lg cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap">
            返回商品列表
          </button>
        </div>
      </div>
    );
  }

  const discount = Math.round((1 - product.price / product.originalPrice) * 100);
  const allImages = product.images || [product.image];

  const productJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": product.description,
      "image": allImages,
      "sku": `CHM-${product.id}`,
      "mpn": `CHM-MPN-${product.id}`,
      "brand": {
        "@type": "Brand",
        "name": product.brand,
      },
      "category": product.category,
      "url": `${SITE_URL}/product/${product.id}`,
      "offers": {
        "@type": "Offer",
        "price": product.price,
        "priceCurrency": "CNY",
        "priceValidUntil": "2026-12-31",
        "availability": product.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        "itemCondition": "https://schema.org/NewCondition",
        "url": `${SITE_URL}/product/${product.id}`,
        "seller": {
          "@type": "Organization",
          "name": "皇冠鸿运商城",
          "url": SITE_URL,
        },
        "shippingDetails": {
          "@type": "OfferShippingDetails",
          "shippingRate": {
            "@type": "MonetaryAmount",
            "value": "0",
            "currency": "CNY",
          },
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": {
              "@type": "QuantitativeValue",
              "minValue": 0,
              "maxValue": 1,
              "unitCode": "DAY",
            },
            "transitTime": {
              "@type": "QuantitativeValue",
              "minValue": 1,
              "maxValue": 3,
              "unitCode": "DAY",
            },
          },
          "shippingDestination": {
            "@type": "DefinedRegion",
            "addressCountry": "CN",
          },
        },
        "hasMerchantReturnPolicy": {
          "@type": "MerchantReturnPolicy",
          "applicableCountry": "CN",
          "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
          "merchantReturnDays": 7,
          "returnMethod": "https://schema.org/ReturnByMail",
          "returnFees": "https://schema.org/FreeReturn",
        },
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": product.rating,
        "reviewCount": product.reviewCount,
        "bestRating": 5,
        "worstRating": 1,
      },
      "review": [
        {
          "@type": "Review",
          "reviewRating": { "@type": "Rating", "ratingValue": 5, "bestRating": 5 },
          "author": { "@type": "Person", "name": "王**" },
          "datePublished": "2026-03-15",
          "reviewBody": "非常好用！质量超出预期，包装精美，物流很快，强烈推荐！",
        },
        {
          "@type": "Review",
          "reviewRating": { "@type": "Rating", "ratingValue": 5, "bestRating": 5 },
          "author": { "@type": "Person", "name": "李**" },
          "datePublished": "2026-03-10",
          "reviewBody": "正品保障，价格实惠，比实体店便宜很多，下次还会来买！",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "首页", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "商品列表", "item": `${SITE_URL}/products` },
        { "@type": "ListItem", "position": 3, "name": product.category, "item": `${SITE_URL}/products?category=${encodeURIComponent(product.category)}` },
        { "@type": "ListItem", "position": 4, "name": product.name, "item": `${SITE_URL}/product/${product.id}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": `${product.name} | ${product.brand} | 皇冠鸿运商城`,
      "description": `${product.name}，${product.brand}，¥${product.price}，${product.sales.toLocaleString()}人购买，评分${product.rating}。正品保障，极速配送，7天退换。`,
      "url": `${SITE_URL}/product/${product.id}`,
      "inLanguage": "zh-CN",
      "isPartOf": {
        "@type": "WebSite",
        "name": "皇冠鸿运商城",
        "url": SITE_URL,
      },
      "publisher": {
        "@type": "Organization",
        "name": "皇冠鸿运商城",
        "logo": {
          "@type": "ImageObject",
          "url": "https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png",
        },
      },
    },
  ];

  const handleAddToCart = () => {
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const reviews = [
    { id: 1, user: "王**", avatar: "W", rating: 5, date: "2026-03-15", content: "非常好用！质量超出预期，包装精美，物流很快，强烈推荐！", color: product.specs.colors[0], storage: product.specs.storages[0] },
    { id: 2, user: "李**", avatar: "L", rating: 5, date: "2026-03-10", content: "正品保障，价格实惠，比实体店便宜很多，下次还会来买！", color: product.specs.colors[0], storage: product.specs.storages[0] },
    { id: 3, user: "张**", avatar: "Z", rating: 4, date: "2026-02-28", content: "整体不错，就是物流稍微慢了一点，商品本身很满意。", color: product.specs.colors[1] || product.specs.colors[0], storage: product.specs.storages[0] },
    { id: 4, user: "陈**", avatar: "C", rating: 5, date: "2026-02-20", content: "皇冠鸿运商城买东西放心，这款产品用了一段时间了，非常满意！", color: product.specs.colors[0], storage: product.specs.storages[1] || product.specs.storages[0] },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHead
        title={`${product.name} | ${product.brand} | 皇冠鸿运商城`}
        description={`${product.name}，${product.brand}，¥${product.price}，${product.sales.toLocaleString()}人购买，评分${product.rating}。正品保障，极速配送，7天退换。`}
        keywords={`${product.name},${product.brand},${product.category},正品,皇冠鸿运商城`}
        canonical={`/product/${product.id}`}
        ogImage={allImages[0]}
        jsonLd={productJsonLd}
      />
      {/* Top bar */}
      <div className="bg-red-600 text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span>皇冠鸿运商城 — 正品保障 · 极速配送</span>
          <div className="flex items-center gap-4">
            <a href="/" className="hover:underline cursor-pointer flex items-center gap-1"><i className="ri-home-line"></i> 首页</a>
            <a href="/products" className="hover:underline cursor-pointer">商品列表</a>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <a href="/" className="flex items-center gap-2 flex-shrink-0">
            <img src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png" alt="Logo" className="h-9 w-auto object-contain" />
            <span className="text-red-600 font-bold text-lg hidden sm:block whitespace-nowrap" style={{ fontFamily: "'Noto Serif SC', serif" }}>皇冠鸿运商城</span>
          </a>
          <div className="flex-1 max-w-xl">
            <div className="flex items-center border-2 border-red-500 rounded-md overflow-hidden">
              <input type="text" placeholder="搜索商品..." className="flex-1 px-4 py-2 text-sm outline-none" />
              <button className="bg-red-500 text-white px-5 py-2 text-sm font-medium whitespace-nowrap cursor-pointer"><i className="ri-search-line mr-1"></i>搜索</button>
            </div>
          </div>
          <button onClick={() => navigate("/products")} className="flex items-center gap-1 text-gray-600 hover:text-red-500 cursor-pointer text-sm whitespace-nowrap ml-auto">
            <i className="ri-arrow-left-line"></i> 返回列表
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-5 flex-wrap">
          <a href="/" className="hover:text-red-500 cursor-pointer">首页</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href="/products" className="hover:text-red-500 cursor-pointer">商品列表</a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-500">{product.category}</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-red-500 line-clamp-1">{product.name}</span>
        </div>

        {/* Main product section */}
        <div className="bg-white rounded-2xl p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Images */}
            <div className="lg:w-[420px] flex-shrink-0">
              <div className="relative rounded-xl overflow-hidden mb-3 bg-gray-50" style={{ height: "380px" }}>
                <img
                  src={allImages[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
                <span className={`absolute top-3 left-3 ${tagColors[product.tag] || "bg-gray-500"} text-white text-xs px-2 py-0.5 rounded-full font-medium`}>
                  {product.tag}
                </span>
                {product.stock <= 10 && (
                  <span className="absolute top-3 right-3 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                    仅剩 {product.stock} 件
                  </span>
                )}
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors cursor-pointer ${selectedImage === idx ? "border-red-500" : "border-gray-200 hover:border-red-300"}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3 mb-3">
                <h1 className="text-gray-900 font-bold text-xl leading-snug">{product.name}</h1>
                <button className="flex-shrink-0 w-9 h-9 flex items-center justify-center border border-gray-200 rounded-full hover:border-red-400 hover:text-red-500 cursor-pointer transition-colors text-gray-400">
                  <i className="ri-heart-line"></i>
                </button>
              </div>

              {/* Rating & sales */}
              <div className="flex items-center gap-4 mb-4 flex-wrap">
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className={`text-sm ${i < Math.floor(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`}></i>
                  ))}
                  <span className="text-amber-500 font-bold text-sm ml-1">{product.rating}</span>
                </div>
                <span className="text-gray-300">|</span>
                <span className="text-gray-400 text-sm">{product.reviewCount.toLocaleString()} 条评价</span>
                <span className="text-gray-300">|</span>
                <span className="text-gray-400 text-sm">{product.sales.toLocaleString()} 人购买</span>
                <span className="text-gray-300">|</span>
                <span className="text-gray-500 text-sm font-medium">{product.brand}</span>
              </div>

              {/* Price */}
              <div className="bg-red-50 rounded-xl p-4 mb-5">
                <div className="flex items-end gap-3 mb-2">
                  <span className="text-red-500 font-bold text-4xl">¥{product.price.toLocaleString()}</span>
                  <span className="text-gray-400 text-base line-through mb-1">¥{product.originalPrice.toLocaleString()}</span>
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold mb-1">省 {discount}%</span>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="bg-red-100 text-red-500 text-xs px-2 py-0.5 rounded-full">满 999 减 100</span>
                  <span className="bg-red-100 text-red-500 text-xs px-2 py-0.5 rounded-full">新人专享 9 折</span>
                  <span className="bg-orange-100 text-orange-500 text-xs px-2 py-0.5 rounded-full">免费配送</span>
                </div>
              </div>

              {/* Color selection */}
              <div className="mb-4">
                <p className="text-gray-700 font-medium text-sm mb-2">
                  颜色：<span className="text-red-500">{product.specs.colors[selectedColor]}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.specs.colors.map((color, idx) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(idx)}
                      className={`px-4 py-2 rounded-lg text-sm border-2 transition-all cursor-pointer whitespace-nowrap ${selectedColor === idx ? "border-red-500 bg-red-50 text-red-500 font-medium" : "border-gray-200 text-gray-600 hover:border-red-300"}`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>

              {/* Storage / size selection */}
              <div className="mb-5">
                <p className="text-gray-700 font-medium text-sm mb-2">
                  规格：<span className="text-red-500">{product.specs.storages[selectedStorage]}</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {product.specs.storages.map((storage, idx) => (
                    <button
                      key={storage}
                      onClick={() => setSelectedStorage(idx)}
                      className={`px-4 py-2 rounded-lg text-sm border-2 transition-all cursor-pointer whitespace-nowrap ${selectedStorage === idx ? "border-red-500 bg-red-50 text-red-500 font-medium" : "border-gray-200 text-gray-600 hover:border-red-300"}`}
                    >
                      {storage}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div className="mb-6">
                <p className="text-gray-700 font-medium text-sm mb-2">数量：</p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-9 h-9 flex items-center justify-center hover:bg-gray-50 cursor-pointer transition-colors text-gray-600"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <span className="w-12 text-center text-sm font-medium">{quantity}</span>
                    <button
                      onClick={() => setQuantity((q) => Math.min(product.stock, q + 1))}
                      className="w-9 h-9 flex items-center justify-center hover:bg-gray-50 cursor-pointer transition-colors text-gray-600"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                  <span className="text-gray-400 text-xs">库存 {product.stock} 件</span>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={handleAddToCart}
                  className={`flex-1 min-w-[140px] py-3.5 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${addedToCart ? "bg-green-500 text-white" : "bg-red-50 border-2 border-red-500 text-red-500 hover:bg-red-100"}`}
                >
                  {addedToCart ? <><i className="ri-check-line mr-1"></i>已加入购物车</> : <><i className="ri-shopping-cart-line mr-1"></i>加入购物车</>}
                </button>
                <button className="flex-1 min-w-[140px] bg-red-500 hover:bg-red-600 text-white py-3.5 rounded-xl font-bold text-sm transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-flashlight-line mr-1"></i>立即购买
                </button>
              </div>

              {/* CrownChat Share */}
              <CrownChatShare productName={product.name} productPrice={product.price} productImage={allImages[0]} />

              {/* Factory Direct Certification Badge */}
              <div className="mt-5 border border-green-200 rounded-xl overflow-hidden">
                <div className="bg-gradient-to-r from-green-500 to-emerald-500 px-4 py-2.5 flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-verified-badge-fill text-white text-base"></i>
                  </div>
                  <span className="text-white font-bold text-sm">工厂直营认证商品</span>
                  <span className="ml-auto bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">平台核验</span>
                </div>
                <div className="bg-green-50 px-4 py-3">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {[
                      { icon: "ri-building-line", label: "工厂名称", value: "极光户外装备有限公司" },
                      { icon: "ri-map-pin-line", label: "工厂地址", value: "浙江省杭州市余杭区" },
                      { icon: "ri-award-line", label: "商标注册", value: "极光户外 第25类" },
                      { icon: "ri-file-shield-line", label: "生产许可", value: "XK16-001-XXXXX" },
                    ].map(item => (
                      <div key={item.label} className="flex items-start gap-2">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                          <i className={`${item.icon} text-green-500 text-xs`}></i>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">{item.label}</p>
                          <p className="text-gray-700 text-xs font-medium">{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 pt-2 border-t border-green-200 flex-wrap">
                    {[
                      { icon: "ri-checkbox-circle-line", text: "营业执照已核验" },
                      { icon: "ri-checkbox-circle-line", text: "仓库实景已审核" },
                      { icon: "ri-checkbox-circle-line", text: "品牌证书已认证" },
                      { icon: "ri-checkbox-circle-line", text: "货款平台托管" },
                    ].map(b => (
                      <span key={b.text} className="flex items-center gap-1 text-green-600 text-xs">
                        <i className={b.icon}></i>{b.text}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Guarantees */}
              <div className="flex items-center gap-4 mt-5 pt-5 border-t border-gray-100 flex-wrap">
                {[
                  { icon: "ri-shield-check-line", text: "正品保障" },
                  { icon: "ri-truck-line", text: "极速配送" },
                  { icon: "ri-refresh-line", text: "7天退换" },
                  { icon: "ri-customer-service-line", text: "24h客服" },
                ].map((g) => (
                  <div key={g.text} className="flex items-center gap-1.5 text-gray-500 text-xs">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${g.icon} text-red-400`}></i>
                    </div>
                    <span>{g.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs: Detail & Reviews */}
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="flex border-b border-gray-100">
            {(["detail", "reviews"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-8 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab ? "text-red-500 border-b-2 border-red-500" : "text-gray-500 hover:text-gray-700"}`}
              >
                {tab === "detail" ? "商品详情" : `用户评价 (${product.reviewCount.toLocaleString()})`}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === "detail" ? (
              <div>
                {/* Highlights */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-800 mb-3">核心亮点</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.highlights.map((h) => (
                      <span key={h} className="bg-red-50 text-red-500 text-sm px-3 py-1.5 rounded-full font-medium">
                        <i className="ri-check-line mr-1"></i>{h}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mb-6">
                  <h3 className="font-bold text-gray-800 mb-3">商品描述</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{product.description}</p>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-3">商品图片</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {allImages.map((img, idx) => (
                      <img key={idx} src={img} alt={`商品图 ${idx + 1}`} className="w-full rounded-xl object-cover object-top" style={{ height: "280px" }} />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                {/* Rating summary */}
                <div className="flex items-center gap-8 mb-6 p-5 bg-red-50 rounded-xl">
                  <div className="text-center">
                    <p className="text-5xl font-bold text-red-500">{product.rating}</p>
                    <div className="flex items-center justify-center gap-0.5 my-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i key={i} className={`text-sm ${i < Math.floor(product.rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`}></i>
                      ))}
                    </div>
                    <p className="text-gray-400 text-xs">{product.reviewCount.toLocaleString()} 条评价</p>
                  </div>
                  <div className="flex-1">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const pct = star === 5 ? 78 : star === 4 ? 15 : star === 3 ? 5 : star === 2 ? 1 : 1;
                      return (
                        <div key={star} className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-500 w-4">{star}</span>
                          <i className="ri-star-fill text-amber-400 text-xs"></i>
                          <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                            <div className="bg-amber-400 h-1.5 rounded-full" style={{ width: `${pct}%` }}></div>
                          </div>
                          <span className="text-xs text-gray-400 w-8">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Review list */}
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 flex items-center justify-center bg-red-100 rounded-full text-red-500 font-bold text-sm flex-shrink-0">
                          {review.avatar}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-gray-700 font-medium text-sm">{review.user}</span>
                            <span className="text-gray-400 text-xs">{review.date}</span>
                          </div>
                          <div className="flex items-center gap-1 mb-1">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <i key={i} className={`text-xs ${i < review.rating ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-300"}`}></i>
                            ))}
                          </div>
                          <p className="text-xs text-gray-400 mb-2">{review.color} · {review.storage}</p>
                          <p className="text-gray-600 text-sm leading-relaxed">{review.content}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related products */}
        <div className="mt-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">相关推荐</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {allProducts.filter((p) => p.id !== product.id && p.category === product.category).slice(0, 6).map((p) => (
              <div
                key={p.id}
                onClick={() => { navigate(`/product/${p.id}`); window.scrollTo(0, 0); }}
                className="bg-white rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-200"
              >
                <img src={p.image} alt={p.name} className="w-full h-32 object-cover object-top" />
                <div className="p-2">
                  <p className="text-gray-700 text-xs line-clamp-2 mb-1">{p.name}</p>
                  <span className="text-red-500 font-bold text-sm">¥{p.price.toLocaleString()}</span>
                </div>
              </div>
            ))}
            {allProducts.filter((p) => p.id !== product.id && p.category === product.category).length === 0 &&
              allProducts.filter((p) => p.id !== product.id).slice(0, 6).map((p) => (
                <div
                  key={p.id}
                  onClick={() => { navigate(`/product/${p.id}`); window.scrollTo(0, 0); }}
                  className="bg-white rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-200"
                >
                  <img src={p.image} alt={p.name} className="w-full h-32 object-cover object-top" />
                  <div className="p-2">
                    <p className="text-gray-700 text-xs line-clamp-2 mb-1">{p.name}</p>
                    <span className="text-red-500 font-bold text-sm">¥{p.price.toLocaleString()}</span>
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </div>
  );
}
